import {definePreset} from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';

/** Charte 17.45 — rouge primaire ; teintes 700+ pour le contraste RGAA sur fond clair. */
const brand1745 = {
	50: '#FEF3F2',
	100: '#FDE5E2',
	200: '#F9CCC6',
	300: '#F3A59A',
	400: '#EC7464',
	500: '#E43726',
	600: '#C92F20',
	700: '#A8261A',
	800: '#881E14',
	900: '#6B1710',
	950: '#4A100B',
};

const warm = {
	0: '#FFFFFF',
	50: '#FEFAF6',
	100: '#FDEBDB',
	200: '#F5DCC8',
	300: '#E8C9B0',
	400: '#C9A892',
	500: '#9A8476',
	600: '#756258',
	700: '#4B2B2B',
	800: '#3A2121',
	900: '#2A1818',
	950: '#1A0F0F',
};

const rose1745 = {
	50: '#FEF1F6',
	100: '#FCE0ED',
	200: '#F9C4DE',
	300: '#F5A8CD',
	400: '#ED84B1',
	500: '#E66A9D',
	600: '#C94D7F',
	700: '#A63565',
	800: '#85234E',
	900: '#64163A',
	950: '#420D26',
};

export const AppPreset = definePreset(Aura, {
	primitive: {
		rose: brand1745,
		rose1745,
		warm,
		appGreen: {
			50: '#EBFCE0',
			100: '#D3F9C3',
			200: '#B1EEA1',
			300: '#8FDE84',
			400: '#60C95C',
			500: '#43AC48',
			600: '#2E903B',
			700: '#1D7430',
			800: '#116029',
			900: '#0A4A1E',
			950: '#063314',
		},
		appBlue: {
			50: '#EBF8FF',
			100: '#D1EFFF',
			200: '#A8DFFF',
			300: '#65C4FD',
			400: '#00B0FC',
			500: '#0088D8',
			600: '#0066B5',
			700: '#004892',
			800: '#003478',
			900: '#002656',
			950: '#001A3D',
		},
		appYellow: {
			50: '#FFFBEB',
			100: '#FFF5C2',
			200: '#FFED94',
			300: '#FFE56B',
			400: '#FFD445',
			500: '#E6BE3D',
			600: '#C4A032',
			700: '#9A7D26',
			800: '#75601C',
			900: '#524214',
			950: '#3A2E0E',
		},
		appRed: {
			50: '#FEF3F2',
			100: '#FDE5E2',
			200: '#F9CCC6',
			300: '#F3A59A',
			400: '#EC7464',
			500: '#E43726',
			600: '#C92F20',
			700: '#A8261A',
			800: '#881E14',
			900: '#6B1710',
			950: '#4A100B',
		},
	},
	semantic: {
		primary: {
			50: '{rose.50}',
			100: '{rose.100}',
			200: '{rose.200}',
			300: '{rose.300}',
			400: '{rose.400}',
			500: '{rose.500}',
			600: '{rose.600}',
			700: '{rose.700}',
			800: '{rose.800}',
			900: '{rose.900}',
			950: '{rose.950}',
		},
		success: {
			50: '{appGreen.50}',
			100: '{appGreen.100}',
			200: '{appGreen.200}',
			300: '{appGreen.300}',
			400: '{appGreen.400}',
			500: '{appGreen.500}',
			600: '{appGreen.600}',
			700: '{appGreen.700}',
			800: '{appGreen.800}',
			900: '{appGreen.900}',
			950: '{appGreen.950}',
		},
		info: {
			50: '{appBlue.50}',
			100: '{appBlue.100}',
			200: '{appBlue.200}',
			300: '{appBlue.300}',
			400: '{appBlue.400}',
			500: '{appBlue.500}',
			600: '{appBlue.600}',
			700: '{appBlue.700}',
			800: '{appBlue.800}',
			900: '{appBlue.900}',
			950: '{appBlue.950}',
		},
		warn: {
			50: '{appYellow.50}',
			100: '{appYellow.100}',
			200: '{appYellow.200}',
			300: '{appYellow.300}',
			400: '{appYellow.400}',
			500: '{appYellow.500}',
			600: '{appYellow.600}',
			700: '{appYellow.700}',
			800: '{appYellow.800}',
			900: '{appYellow.900}',
			950: '{appYellow.950}',
		},
		danger: {
			50: '{appRed.50}',
			100: '{appRed.100}',
			200: '{appRed.200}',
			300: '{appRed.300}',
			400: '{appRed.400}',
			500: '{appRed.500}',
			600: '{appRed.600}',
			700: '{appRed.700}',
			800: '{appRed.800}',
			900: '{appRed.900}',
			950: '{appRed.950}',
		},
		fontFamily: {
			sans: '"Helvetica Neue", Helvetica, Arial, sans-serif',
			display: '"Barlow Condensed", "Helvetica Neue", Helvetica, Arial, sans-serif',
		},
		borderRadius: {
			none: '0',
			xs: '3px',
			sm: '6px',
			md: '8px',
			lg: '12px',
			xl: '16px',
		},
		focusRing: {
			width: '3px',
			style: 'solid',
			color: '{primary.700}',
			offset: '2px',
			shadow: 'none',
		},
		formField: {
			paddingX: '0.875rem',
			paddingY: '0.625rem',
			sm: {
				fontSize: '0.875rem',
				paddingX: '0.75rem',
				paddingY: '0.5rem',
			},
			lg: {
				fontSize: '1.0625rem',
				paddingX: '1rem',
				paddingY: '0.75rem',
			},
			focusRing: {
				width: '3px',
				style: 'solid',
				color: '{primary.700}',
				offset: '1px',
				shadow: 'none',
			},
		},
		colorScheme: {
			light: {
				primary: {
					color: '{primary.800}',
					contrastColor: '#FFFFFF',
					hoverColor: '{primary.900}',
					activeColor: '{primary.950}',
				},
				highlight: {
					background: '{primary.100}',
					focusBackground: '{primary.200}',
					color: '{primary.900}',
					focusColor: '{primary.950}',
				},
				surface: {
					0: '{warm.0}',
					50: '{warm.50}',
					100: '{warm.100}',
					200: '{warm.200}',
					300: '{warm.300}',
					400: '{warm.400}',
					500: '{warm.500}',
					600: '{warm.600}',
					700: '{warm.700}',
					800: '{warm.800}',
					900: '{warm.900}',
					950: '{warm.950}',
				},
				formField: {
					background: '{surface.0}',
					disabledBackground: '{surface.100}',
					filledBackground: '{surface.50}',
					filledHoverBackground: '{surface.100}',
					filledFocusBackground: '{surface.0}',
					borderColor: '{surface.300}',
					hoverBorderColor: '{surface.400}',
					focusBorderColor: '{primary.700}',
					invalidBorderColor: '{danger.600}',
					color: '{surface.900}',
					disabledColor: '{surface.500}',
					placeholderColor: '{surface.600}',
					invalidPlaceholderColor: '{danger.700}',
					iconColor: '{surface.600}',
					shadow: '0 1px 2px rgba(26, 27, 28, 0.04)',
				},
				text: {
					color: '{surface.900}',
					hoverColor: '{surface.950}',
					mutedColor: '{surface.600}',
					hoverMutedColor: '{surface.700}',
				},
				content: {
					background: '{surface.0}',
					hoverBackground: '{surface.100}',
					borderColor: '{surface.200}',
					color: '{text.color}',
					hoverColor: '{text.hover.color}',
				},
			},
			dark: {
				primary: {
					color: '{primary.300}',
					contrastColor: '{surface.950}',
					hoverColor: '{primary.200}',
					activeColor: '{primary.100}',
				},
				highlight: {
					background: 'color-mix(in srgb, {primary.400}, transparent 82%)',
					focusBackground: 'color-mix(in srgb, {primary.400}, transparent 72%)',
					color: '{primary.100}',
					focusColor: '{primary.50}',
				},
				surface: {
					0: '#F5F2EE',
					50: '#E8E4E0',
					100: '#D4CFC8',
					200: '#B8B2AA',
					300: '#989490',
					400: '#787470',
					500: '#686A6C',
					600: '#525456',
					700: '#3D3F41',
					800: '#2A2C2E',
					900: '#1C1917',
					950: '#100E0C',
				},
				formField: {
					background: '{surface.900}',
					disabledBackground: '{surface.800}',
					filledBackground: '{surface.800}',
					filledHoverBackground: '{surface.700}',
					filledFocusBackground: '{surface.900}',
					borderColor: '{surface.600}',
					hoverBorderColor: '{surface.500}',
					focusBorderColor: '{primary.300}',
					invalidBorderColor: '{danger.400}',
					color: '{surface.0}',
					disabledColor: '{surface.200}',
					placeholderColor: '{surface.200}',
					invalidPlaceholderColor: '{danger.300}',
					iconColor: '{surface.100}',
					shadow: '0 1px 2px rgba(0, 0, 0, 0.24)',
				},
				text: {
					color: '{surface.0}',
					hoverColor: '#FFFFFF',
					mutedColor: '{surface.100}',
					hoverMutedColor: '{surface.50}',
				},
				content: {
					background: '{surface.900}',
					hoverBackground: '{surface.800}',
					borderColor: '{surface.700}',
					color: '{text.color}',
					hoverColor: '{text.hover.color}',
				},
			},
		},
	},
	components: {
		button: {
			root: {
				label: {
					fontWeight: '600',
				},
				borderRadius: '{border.radius.md}',
			},
			colorScheme: {
				light: {
					root: {
						primary: {
							background: '{primary.800}',
							hoverBackground: '{primary.900}',
							activeBackground: '{primary.950}',
							borderColor: '{primary.800}',
							hoverBorderColor: '{primary.900}',
							activeBorderColor: '{primary.950}',
							color: '#FFFFFF',
							focusRing: {
								color: '{primary.700}',
								shadow: '0 0 0 3px color-mix(in srgb, {primary.400}, transparent 60%)',
							},
						},
						success: {
							background: '{success.600}',
							hoverBackground: '{success.700}',
							activeBackground: '{success.800}',
							borderColor: '{success.600}',
							color: '#FFFFFF',
						},
						danger: {
							background: '{danger.600}',
							hoverBackground: '{danger.700}',
							activeBackground: '{danger.800}',
							borderColor: '{danger.600}',
							color: '#FFFFFF',
						},
						warn: {
							background: '{warn.600}',
							hoverBackground: '{warn.700}',
							activeBackground: '{warn.800}',
							borderColor: '{warn.600}',
							color: '#FFFFFF',
						},
						info: {
							background: '{info.600}',
							hoverBackground: '{info.700}',
							activeBackground: '{info.800}',
							borderColor: '{info.600}',
							color: '#FFFFFF',
						},
					},
					outlined: {
						primary: {
							borderColor: '{primary.700}',
							color: '{primary.800}',
							hoverBackground: '{primary.100}',
							activeBackground: '{primary.200}',
						},
						secondary: {
							borderColor: '{surface.400}',
							color: '{surface.700}',
							hoverBackground: '{surface.100}',
							activeBackground: '{surface.200}',
						},
						success: {
							borderColor: '{success.600}',
							color: '{success.700}',
							hoverBackground: '{success.50}',
							activeBackground: '{success.100}',
						},
						danger: {
							borderColor: '{danger.600}',
							color: '{danger.700}',
							hoverBackground: '{danger.50}',
							activeBackground: '{danger.100}',
						},
						warn: {
							borderColor: '{warn.700}',
							color: '{warn.800}',
							hoverBackground: '{warn.50}',
							activeBackground: '{warn.100}',
						},
						info: {
							borderColor: '{info.600}',
							color: '{info.700}',
							hoverBackground: '{info.50}',
							activeBackground: '{info.100}',
						},
					},
				},
				dark: {
					root: {
						primary: {
							background: '{primary.300}',
							hoverBackground: '{primary.200}',
							activeBackground: '{primary.100}',
							borderColor: '{primary.300}',
							color: '{surface.950}',
						},
					},
					outlined: {
						primary: {
							borderColor: '{primary.300}',
							color: '{primary.200}',
							hoverBackground: 'color-mix(in srgb, {primary.400}, transparent 86%)',
							activeBackground: 'color-mix(in srgb, {primary.400}, transparent 72%)',
						},
						secondary: {
							borderColor: '{surface.500}',
							color: '{surface.100}',
							hoverBackground: '{surface.800}',
							activeBackground: '{surface.700}',
						},
						success: {
							borderColor: '{success.400}',
							color: '{success.200}',
							hoverBackground: 'color-mix(in srgb, {success.400}, transparent 86%)',
							activeBackground: 'color-mix(in srgb, {success.400}, transparent 72%)',
						},
						danger: {
							borderColor: '{danger.400}',
							color: '{danger.200}',
							hoverBackground: 'color-mix(in srgb, {danger.400}, transparent 86%)',
							activeBackground: 'color-mix(in srgb, {danger.400}, transparent 72%)',
						},
						warn: {
							borderColor: '{warn.400}',
							color: '{warn.200}',
							hoverBackground: 'color-mix(in srgb, {warn.400}, transparent 86%)',
							activeBackground: 'color-mix(in srgb, {warn.400}, transparent 72%)',
						},
						info: {
							borderColor: '{info.400}',
							color: '{info.200}',
							hoverBackground: 'color-mix(in srgb, {info.400}, transparent 86%)',
							activeBackground: 'color-mix(in srgb, {info.400}, transparent 72%)',
						},
					},
				},
			},
		},
		card: {
			colorScheme: {
				light: {
					root: {
						background: '{surface.0}',
						color: '{text.color}',
						borderRadius: '{border.radius.lg}',
						shadow: '0 1px 3px rgba(26, 27, 28, 0.06), 0 1px 2px rgba(26, 27, 28, 0.04)',
					},
					body: {
						padding: '1.25rem 1.5rem',
					},
					title: {
						fontSize: '1.125rem',
						fontWeight: '600',
					},
				},
				dark: {
					root: {
						background: '{surface.900}',
						color: '{text.color}',
						borderRadius: '{border.radius.lg}',
						shadow: '0 4px 12px rgba(0, 0, 0, 0.32)',
					},
				},
			},
		},
		inputtext: {
			root: {
				paddingX: '{form.field.padding.x}',
				paddingY: '{form.field.padding.y}',
				borderRadius: '{border.radius.md}',
				focusRing: {
					width: '3px',
					style: 'solid',
					color: '{primary.700}',
					offset: '1px',
					shadow: 'none',
				},
			},
		},
		datatable: {
			headerCell: {
				background: '{surface.100}',
				color: '{text.color}',
			},
			colorScheme: {
				dark: {
					headerCell: {
						background: '{surface.800}',
					},
				},
			},
		},
	},
	extend: {
		app: {
			card: {marginBottom: '1.875rem'},
			pageBackground: '{warm.100}',
			pageBackgroundDark: '{surface.950}',
			shellBackground: '{warm.100}',
			shellBackgroundDark: '{surface.900}',
			sidebarBackground: '{warm.100}',
			sidebarBackgroundDark: '{surface.950}',
			menuFlyoutBackground: '{warm.0}',
			menuFlyoutBackgroundDark: '{surface.900}',
			chat: {
				largeHeight: '36rem',
				mediumHeight: '28rem',
				background: '{surface.0}',
				backgroundDark: '{surface.900}',
				borderColor: '{content.border.color}',
				messageBackground: '{surface.100}',
				messageBackgroundDark: '{surface.800}',
				messageReplyBackground: '{rose1745.100}',
				messageReplyBackgroundDark: '{primary.800}',
				messageTextColor: '{text.color}',
				messageReplyTextColor: '{text.color}',
				senderColor: '{text.muted.color}',
			},
		},
	},
	css: ({dt}) => `
		:root {
			--app-page-bg: ${dt('app.pageBackground')};
			--app-shell-bg: ${dt('app.shellBackground')};
			--app-sidebar-bg: ${dt('app.sidebarBackground')};
			--app-brand: ${dt('primary.700')};
			--app-brand-light: ${dt('rose1745.400')};
			--app-brand-soft: ${dt('warm.100')};
			--app-brand-beige: ${dt('warm.100')};
			--app-brand-marron: ${dt('warm.700')};
			--app-font-display: ${dt('fontFamily.display')};
			--app-menu-flyout-bg: ${dt('app.menuFlyoutBackground')};
			--app-menu-flyout-border: ${dt('content.border.color')};
			--app-menu-flyout-z: 1500;
			--app-focus-ring: 0 0 0 3px color-mix(in srgb, ${dt('primary.400')}, transparent 55%);
			--app-chat-bg: ${dt('app.chat.background')};
			--app-chat-border: ${dt('app.chat.borderColor')};
			--app-chat-msg-bg: ${dt('app.chat.messageBackground')};
			--app-chat-msg-reply-bg: ${dt('app.chat.messageReplyBackground')};
			--app-chat-msg-color: ${dt('app.chat.messageTextColor')};
			--app-chat-msg-reply-color: ${dt('app.chat.messageReplyTextColor')};
			--app-chat-sender-color: ${dt('app.chat.senderColor')};
			--app-chat-large-height: ${dt('app.chat.largeHeight')};
		}

		html.app-dark {
			--app-page-bg: ${dt('app.pageBackgroundDark')};
			--app-shell-bg: ${dt('app.shellBackgroundDark')};
			--app-sidebar-bg: color-mix(in srgb, ${dt('primary.950')}, ${dt('surface.950')} 72%);
			--app-brand: ${dt('primary.300')};
			--app-brand-light: ${dt('primary.400')};
			--app-brand-soft: color-mix(in srgb, ${dt('primary.900')}, transparent 55%);
			--app-menu-flyout-bg: ${dt('app.menuFlyoutBackgroundDark')};
			--app-menu-flyout-border: ${dt('content.border.color')};
			--app-focus-ring: 0 0 0 3px color-mix(in srgb, ${dt('primary.300')}, transparent 45%);
			--app-chat-bg: ${dt('app.chat.backgroundDark')};
			--app-chat-msg-bg: ${dt('app.chat.messageBackgroundDark')};
			--app-chat-msg-reply-bg: ${dt('app.chat.messageReplyBackgroundDark')};
		}

		html {
			font-size: 16px;
			-webkit-font-smoothing: antialiased;
			-moz-osx-font-smoothing: grayscale;
		}

		body {
			font-family: ${dt('fontFamily.sans')};
			line-height: 1.5;
			color: ${dt('text.color')};
			background: var(--app-page-bg);
		}

		h1, h2, h3, h4, h5, h6,
		.p-card-title,
		.app-display-title {
			font-family: ${dt('fontFamily.display')};
			font-weight: 600;
			letter-spacing: -0.035em;
			text-transform: uppercase;
		}

		h1 { font-size: 1.875rem; }
		h2 { font-size: 1.5rem; }
		h3 { font-size: 1.25rem; }

		.p-card {
			margin-bottom: ${dt('app.card.marginBottom')};
			border: 1px solid ${dt('content.border.color')};
		}

		.p-menu-overlay,
		.p-menu-list {
			background: ${dt('app.menuFlyoutBackground')} !important;
			border: 1px solid ${dt('content.border.color')};
			box-shadow: 0 12px 40px color-mix(in srgb, ${dt('surface.900')}, transparent 82%);
		}

		html.app-dark .p-menu-overlay,
		html.app-dark .p-menu-list {
			background: ${dt('app.menuFlyoutBackgroundDark')} !important;
			box-shadow: 0 12px 40px rgba(0, 0, 0, 0.45);
		}

		.p-inputtext,
		.p-select,
		.p-multiselect,
		.p-textarea {
			min-height: 2.75rem;
		}

		.p-inputtext:focus-visible,
		.p-select:focus-visible,
		.p-multiselect:focus-visible,
		.p-textarea:focus-visible,
		.p-button:focus-visible,
		a:focus-visible,
		button:focus-visible {
			outline: none;
			box-shadow: var(--app-focus-ring);
		}

		.p-datatable .p-datatable-thead > tr > th {
			font-size: 0.8125rem;
			letter-spacing: 0.02em;
			text-transform: uppercase;
		}

		@media (prefers-reduced-motion: reduce) {
			*, *::before, *::after {
				animation-duration: 0.01ms !important;
				animation-iteration-count: 1 !important;
				transition-duration: 0.01ms !important;
			}
		}
	`,
});

export const APP_DARK_CLASS = 'app-dark';
